import { DeleteOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import depositApi from '../../apis/deposit';
import userApi from '../../apis/user';
import { USER_ROLES_CREATION_OPTIONS } from '../../constants/user';
import {
  IAddDepositRequest,
  IDeductDepositRequest,
} from '../../interfaces/Deposit';
import { IUser } from '../../interfaces/UserModel';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import {
  ButtonGroupContainer,
  FlexContainer,
  PageContainer,
} from '../../styles/Global';
import { $background } from '../../styles/Variables';
import DepositTransactionHistory from '../DepositTransactionHistory/DepositTransactionHistory';
import CustomForm from '../Form/Form';
import CustomInput from '../Form/FormItem/Input/Input';
import CustomInputNumber from '../Form/FormItem/InputNumber/InputNumber';
import CustomSelect from '../Form/FormItem/Select/Select';
import BaseModal from '../Modal/BaseModal/BaseModal';
import PageHeader from '../PageHeader/PageHeader';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import CustomTextArea from '../TextArea/TextArea';

// Extended user interface that includes deposit information
interface IUserWithDeposit extends IUser {
  deposit?: number;
}

const ActionsStyled = styled.div`
  border-radius: 8px;
  background-color: ${$background};
  padding: 20px;
  width: 200px;
  position: sticky;
  top: 90px;
`;

const UserDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { authUser } = useSelector(
    (state: RootState) => state.auth as IAuthState
  );
  const isAdmin = authUser?.role === Role.ADMIN;

  const [loading, setLoading] = useState<boolean>(false);
  const isCreating = !id && !loading;
  const [form] = Form.useForm<IUser>();

  const [user, setUser] = useState<IUserWithDeposit>({
    name: '',
    username: '',
    email: '',
    role: Role.SELLER,
    deposit: 0,
  });

  const fetchUser = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        const result = await userApi.getUserInfo(id);
        // Extend the result with deposit information for display
        const userWithDeposit: IUserWithDeposit = {
          ...result,
          deposit: (result as any).deposit || 0, // Safely access deposit from API response
        };
        setUser(userWithDeposit);
        form.setFieldsValue({
          name: result.name,
          email: result.email,
          username: result.username,
          role: result.role,
        });
      } catch (error) {
        navigate(-1);
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [form, navigate]
  );

  useEffect(() => {
    if (!!id) {
      fetchUser(id);
    }
  }, [id, fetchUser]);

  // Deposit form state
  const [depositForm] = Form.useForm<IAddDepositRequest>();
  const [isDepositModalVisible, setIsDepositModalVisible] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);

  // Deduct form state
  const [deductForm] = Form.useForm<IDeductDepositRequest>();
  const [isDeductModalVisible, setIsDeductModalVisible] = useState(false);
  const [deductLoading, setDeductLoading] = useState(false);

  const updateUser = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const { username, name, email } = form.getFieldsValue(true);

      const payload: IUser = {
        username: username || '',
        name: name || '',
        email: email || '',
      };

      await userApi.updateUser(id, payload);
      message.success('Cập nhật người dùng thành công!');
      navigate(-1);
    } catch (e) {
      console.error(e);
      message.error('Cập nhật người dùng thất bại');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    try {
      setLoading(true);
      const payload: IUser = form.getFieldsValue(true);

      await userApi.createUser(payload);
      message.success('Tạo người dùng thành công!');
      navigate(-1);
    } catch (e) {
      console.error(e);
      message.error('Tạo người dùng thất bại');
    } finally {
      setLoading(false);
    }
  };

  const addDeposit = async () => {
    if (!id) {
      message.error('Cần có ID người dùng');
      return;
    }

    try {
      setDepositLoading(true);
      const values = depositForm.getFieldsValue(true);

      const payload: IAddDepositRequest = {
        sellerId: id,
        amount: values.amount,
        notes: values.notes,
      };

      await depositApi.addDeposit(payload);
      message.success('Thêm tiền nạp thành công!');

      // Refresh user data to show updated deposit balance
      fetchUser(id);

      // Reset form and close modal
      depositForm.resetFields();
      setIsDepositModalVisible(false);
    } catch (e) {
      console.error(e);
      message.error('Thêm tiền nạp thất bại');
    } finally {
      setDepositLoading(false);
    }
  };

  const deductDeposit = async () => {
    if (!id) {
      message.error('Cần có ID người dùng');
      return;
    }

    try {
      setDeductLoading(true);
      const values = deductForm.getFieldsValue(true);

      // Check if user has enough balance
      if (values.amount > (user.sellerDeposit?.balance || 0)) {
        message.error('Số dư không đủ để thực hiện giao dịch này');
        return;
      }

      const payload: IDeductDepositRequest = {
        sellerId: id,
        amount: values.amount,
        notes: values.notes,
      };

      await depositApi.deductDeposit(payload);
      message.success('Trừ tiền thành công!');

      // Refresh user data to show updated deposit balance
      fetchUser(id);

      // Reset form and close modal
      deductForm.resetFields();
      setIsDeductModalVisible(false);
    } catch (e) {
      console.error(e);
      message.error('Trừ tiền thất bại');
    } finally {
      setDeductLoading(false);
    }
  };

  return (
    <>
      <PageContainer>
        <PageHeader title="Chi tiết người dùng" showBackButton />
        <div style={{ padding: '0 10px' }}>
          {/* <CustomPageHeader
            className="site-page-header p--0"
            title={id ? programInfoData.name : "New program"}
            onBack={() => navigate(-1)}
          /> */}

          <CustomForm
            form={form}
            initialValues={user}
            layout="vertical"
            scrollToFirstError
            onFinish={() => {
              if (!id) {
                createUser();
                return;
              }

              updateUser();
            }}
          >
            <Form.Item
              name="username"
              label="Tên đăng nhập"
              rules={[
                {
                  required: true,
                  type: 'string',
                  message: 'Vui lòng nhập tên đăng nhập',
                },
              ]}
            >
              <CustomInput placeholder="Tên đăng nhập" />
            </Form.Item>
            <Form.Item
              name="name"
              label="Họ và tên"
              rules={[
                {
                  required: true,
                  type: 'string',
                  message: 'Vui lòng nhập họ và tên',
                },
              ]}
            >
              <CustomInput placeholder="Họ và tên" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  type: 'string',
                  message: 'Vui lòng nhập email hợp lệ',
                },
              ]}
            >
              <CustomInput placeholder="Email" />
            </Form.Item>

            <Form.Item
              name="role"
              label="Vai trò"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn vai trò',
                },
              ]}
            >
              <CustomSelect
                options={USER_ROLES_CREATION_OPTIONS}
                placeholder="Chọn vai trò"
              />
            </Form.Item>

            {isCreating && (
              <>
                <Form.Item
                  name="password"
                  label="Mật khẩu"
                  rules={[
                    {
                      required: true,
                      type: 'string',
                      message:
                        'Vui lòng nhập mật khẩu ít nhất 8 ký tự bao gồm số và chữ',
                    },
                  ]}
                >
                  <CustomInput placeholder="Mật khẩu" type="password" />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  label="Xác nhận mật khẩu"
                  dependencies={['password']}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng xác nhận mật khẩu!',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error('Hai mật khẩu bạn nhập không khớp!')
                        );
                      },
                    }),
                  ]}
                >
                  <CustomInput
                    placeholder="Xác nhận mật khẩu"
                    type="password"
                  />
                </Form.Item>
              </>
            )}

            {/* Display current deposit balance for existing users - visible for ADMIN and EMPLOYEE */}
            {!!id &&
              (authUser?.role === Role.ADMIN ||
                authUser?.role === Role.EMPLOYEE) && (
                <Form.Item label="Số dư hiện tại">
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#1890ff',
                    }}
                  >
                    {(user.sellerDeposit?.balance || 0).toLocaleString('vi-VN')}{' '}
                    VNĐ
                  </div>
                </Form.Item>
              )}

            <Form.Item>
              <ButtonGroupContainer>
                <PrimaryButton htmlType="submit">
                  {isCreating ? 'Tạo mới' : 'Lưu'}
                </PrimaryButton>
              </ButtonGroupContainer>
            </Form.Item>
          </CustomForm>
        </div>

        {!!id && (
          <FlexContainer style={{ padding: '0 10px' }}>
            {/* Deposit Actions - Only for ADMIN users, not EMPLOYEE */}
            {isAdmin && (
              <FlexContainer>
                <ActionsStyled style={{ marginBottom: '20px' }}>
                  <Button
                    className="flex-1 font-weight--500 bg--transparent border--none text-primary text-align--left p__v--4 p__h--11"
                    icon={<PlusOutlined className="font-size--16 p__r--8" />}
                    type="text"
                    onClick={() => setIsDepositModalVisible(true)}
                  >
                    <span className="m__l--10 font-size--16">
                      Thêm tiền nạp
                    </span>
                  </Button>
                </ActionsStyled>
                <ActionsStyled
                  style={{ marginBottom: '20px', marginLeft: '10px' }}
                >
                  <Button
                    className="flex-1 font-weight--500 bg--transparent border--none text-orange text-align--left p__v--4 p__h--11"
                    icon={<MinusOutlined className="font-size--16 p__r--8" />}
                    type="text"
                    onClick={() => setIsDeductModalVisible(true)}
                  >
                    <span className="m__l--10 font-size--16">Trừ tiền</span>
                  </Button>
                </ActionsStyled>
              </FlexContainer>
            )}
            {isAdmin && (
              <ActionsStyled>
                <Button
                  className="w-100 font-weight--300 bg--transparent border--none text-danger text-align--left p__v--4 p__h--11"
                  icon={<DeleteOutlined className="font-size--16 p__r--8" />}
                  type="text"
                >
                  <span className="m__l--10 font-size--16">Xóa người dùng</span>
                </Button>
              </ActionsStyled>
            )}
          </FlexContainer>
        )}

        {/* Transaction History - Show for existing users with ADMIN or EMPLOYEE role */}
        {!!id &&
          (authUser?.role === Role.ADMIN ||
            authUser?.role === Role.EMPLOYEE) && (
            <DepositTransactionHistory userId={id} />
          )}
      </PageContainer>

      {/* Add Deposit Modal */}
      <BaseModal
        visibleModal={isDepositModalVisible}
        title="Thêm tiền nạp"
        primaryBtn="Thêm tiền nạp"
        secondaryBtn="Hủy"
        confirmLoading={depositLoading}
        onPrimaryBtnClick={() => {
          depositForm.submit();
        }}
        onSecondaryBtnClick={() => {
          setIsDepositModalVisible(false);
          depositForm.resetFields();
        }}
        destroyOnClose={true}
      >
        <CustomForm
          form={depositForm}
          layout="vertical"
          requiredMark="optional"
          onFinish={addDeposit}
        >
          <Form.Item
            name="amount"
            label="Số tiền nạp"
            rules={[
              {
                required: true,
                type: 'number',
                min: 1000,
                message:
                  'Vui lòng nhập số tiền nạp hợp lệ (tối thiểu 1.000 VNĐ)',
              },
            ]}
          >
            <CustomInputNumber
              placeholder="Nhập số tiền theo VND (ví dụ: 1000)"
              min={1000}
              precision={0}
              style={{ width: '100%' }}
              addonAfter="VNĐ"
            />
          </Form.Item>

          <Form.Item name="notes" label="Ghi chú (Tùy chọn)">
            <CustomTextArea
              placeholder="Thêm ghi chú về giao dịch nạp tiền này..."
              rows={3}
            />
          </Form.Item>
        </CustomForm>
      </BaseModal>

      {/* Deduct Deposit Modal */}
      <BaseModal
        visibleModal={isDeductModalVisible}
        title="Trừ tiền"
        primaryBtn="Trừ tiền"
        secondaryBtn="Hủy"
        confirmLoading={deductLoading}
        onPrimaryBtnClick={() => {
          deductForm.submit();
        }}
        onSecondaryBtnClick={() => {
          setIsDeductModalVisible(false);
          deductForm.resetFields();
        }}
        destroyOnClose={true}
      >
        <CustomForm
          form={deductForm}
          layout="vertical"
          requiredMark="optional"
          onFinish={deductDeposit}
        >
          <div
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '6px',
            }}
          >
            <div style={{ fontSize: '14px', color: '#fa8c16' }}>
              <strong>
                Số dư hiện tại:{' '}
                {(user.sellerDeposit?.balance || 0).toLocaleString('vi-VN')} VNĐ
              </strong>
            </div>
          </div>

          <Form.Item
            name="amount"
            label="Số tiền trừ"
            rules={[
              {
                required: true,
                type: 'number',
                min: 1000,
                message:
                  'Vui lòng nhập số tiền trừ hợp lệ (tối thiểu 1.000 VNĐ)',
              },
            ]}
          >
            <CustomInputNumber
              placeholder="Nhập số tiền cần trừ theo VND (ví dụ: 1000)"
              min={1000}
              max={user.sellerDeposit?.balance || 0}
              precision={0}
              style={{ width: '100%' }}
              addonAfter="VNĐ"
            />
          </Form.Item>

          <Form.Item name="notes" label="Ghi chú (Tùy chọn)">
            <CustomTextArea
              placeholder="Thêm ghi chú về giao dịch trừ tiền này..."
              rows={3}
            />
          </Form.Item>
        </CustomForm>
      </BaseModal>
    </>
  );
};

export default UserDetail;
